import { useEffect } from 'react'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'

export interface UseSearchResultMarkerProps {
  searchResult?: { id: string; name: string; latitude: number; longitude: number; category?: string; poi_type?: string; address?: string } | null
}

/**
 * Hook for managing search result marker rendering
 * Shows a blue marker for location search results
 */
export function useSearchResultMarker({
  searchResult
}: UseSearchResultMarkerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    if (!searchResult) {
      if (map.current.getLayer('search-result-marker')) {
        map.current.removeLayer('search-result-marker')
      }
      if (map.current.getSource('search-result-marker')) {
        map.current.removeSource('search-result-marker')
      }
      return
    }

    const searchFeature = {
      type: 'Feature' as const,
      properties: {
        id: searchResult.id,
        name: searchResult.name,
        address: searchResult.address || '',
        category: searchResult.category || 'search',
        poi_type: searchResult.poi_type || 'custom'
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [searchResult.longitude, searchResult.latitude]
      }
    }

    const geoJsonData = {
      type: 'FeatureCollection' as const,
      features: [searchFeature]
    }

    if (!map.current?.isStyleLoaded()) return

    try {
      if (map.current.getSource('search-result-marker')) {
        (map.current.getSource('search-result-marker') as mapboxgl.GeoJSONSource).setData(geoJsonData)
      } else {
        map.current.addSource('search-result-marker', {
          type: 'geojson',
          data: geoJsonData
        })

        map.current.addLayer({
          id: 'search-result-marker',
          type: 'circle',
          source: 'search-result-marker',
          paint: {
            'circle-color': '#3b82f6',
            'circle-radius': 12,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 3,
            'circle-opacity': 0.9
          }
        })

        // Ensure search result marker is on top
        const layers = ['waypoint-markers', 'poi-markers', 'route-lines']
        layers.forEach(layerId => {
          if (map.current?.getLayer(layerId)) {
            map.current.moveLayer('search-result-marker', layerId)
          }
        })
      }
    } catch (error) {
      if (map.current?.isStyleLoaded()) {
      }
    }
  }, [map, isLoaded, styleLoaded, searchResult])
}

